import { useEffect, useRef, useState } from 'react'
import {
  Map as MlMap, NavigationControl, Popup, ScaleControl, setWorkerUrl,
  type GeoJSONSource,
} from 'maplibre-gl'
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import 'maplibre-gl/dist/maplibre-gl.css'
import { buildRasterStyle, rasterBasemap } from '../map/basemaps'
import { attachNetradyneCameraHandlers, netradyneCameraButtonHtml } from '../map/netradyneCameraPopup'
import { Panel } from '../cc/Panel'
import type { EimSite, EimVehicle } from './useEimApi'
import { EIM_STATUS_LABEL, EIM_STATUS_STYLE, eimStatusKey, type EimStatusKey } from './statusStyle'

let workerSet = false
function ensureWorker(): void {
  if (workerSet) return
  setWorkerUrl(workerUrl)
  workerSet = true
}

/** Rough fallback centre before the first real fix arrives -- overwritten by fitToVehicles
 *  the moment any vehicle in that site reports a position. */
const SITE_FALLBACK_CENTER: Record<EimSite, [number, number]> = {
  gti: [72.94, 18.95],
  jm_baxi: [70.22, 22.99],
}

// Status classification and colour both come from statusStyle.ts now -- this map used to
// keep its own copy (a red 'alert', a blue 'charging') that quietly drifted from the tile
// grid's colours the moment either file was edited alone. One source for both surfaces.

// ============================================================ truck icon (canvas-drawn)
// Same hand-drawn truck silhouette console/src/map/layers/markers.ts uses for the
// Ultratech corridor map, ported rather than reinvented so a truck looks like a truck the
// same way everywhere in this console. Load state (laden/empty) doesn't apply here --
// these are port-yard container haulers, not a corridor with a laden/empty return leg --
// so every icon is drawn in the solid "laden" style; only the colour carries status.
const ICON_W = 38
const ICON_H = 66
const TYRE = '#1F2937'
const GLASS = 'rgba(255,255,255,0.85)'
const OUTLINE = 'rgba(255,255,255,0.94)'

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2)
  g.beginPath()
  g.moveTo(x + rr, y)
  g.lineTo(x + w - rr, y)
  g.quadraticCurveTo(x + w, y, x + w, y + rr)
  g.lineTo(x + w, y + h - rr)
  g.quadraticCurveTo(x + w, y + h, x + w - rr, y + h)
  g.lineTo(x + rr, y + h)
  g.quadraticCurveTo(x, y + h, x, y + h - rr)
  g.lineTo(x, y + rr)
  g.quadraticCurveTo(x, y, x, y + rr)
  g.closePath()
}

function axle(g: CanvasRenderingContext2D, cy: number, halfTrack: number, len: number): void {
  const tw = 5
  g.fillStyle = TYRE
  roundRect(g, ICON_W / 2 - halfTrack - tw, cy - len / 2, tw, len, 1.8)
  g.fill()
  roundRect(g, ICON_W / 2 + halfTrack, cy - len / 2, tw, len, 1.8)
  g.fill()
}

function truckIcon(color: string, ratio: number): ImageData {
  const c = document.createElement('canvas')
  c.width = ICON_W * ratio
  c.height = ICON_H * ratio
  const g = c.getContext('2d')!
  g.scale(ratio, ratio)
  const cx = ICON_W / 2
  g.lineJoin = 'round'
  g.lineCap = 'round'

  axle(g, 17, 12, 9)
  axle(g, 40, 12.5, 9)
  axle(g, 51, 12.5, 9)
  const deckTop = 27

  const bx = cx - 13, bw = 26, bh = ICON_H - 3 - deckTop
  roundRect(g, bx, deckTop, bw, bh, 3)
  g.fillStyle = color
  g.fill()
  g.strokeStyle = OUTLINE
  g.lineWidth = 1.8
  g.stroke()
  g.strokeStyle = 'rgba(255,255,255,0.5)'
  g.lineWidth = 1.3
  g.beginPath()
  for (const t of [0.3, 0.55, 0.8]) {
    g.moveTo(bx + 3, deckTop + bh * t)
    g.lineTo(bx + bw - 3, deckTop + bh * t)
  }
  g.stroke()

  g.fillStyle = TYRE
  roundRect(g, cx - 16.5, 9.5, 5, 2.6, 1.2)
  g.fill()
  roundRect(g, cx + 11.5, 9.5, 5, 2.6, 1.2)
  g.fill()

  roundRect(g, cx - 12, 3, 24, 25, 4.5)
  g.fillStyle = color
  g.fill()
  g.strokeStyle = OUTLINE
  g.lineWidth = 1.8
  g.stroke()

  roundRect(g, cx - 8, 6.5, 16, 5.5, 2)
  g.fillStyle = GLASS
  g.fill()

  return g.getImageData(0, 0, ICON_W * ratio, ICON_H * ratio)
}

function iconId(status: EimStatusKey): string {
  return `eim-truck-${status}`
}

function registerTruckIcons(map: MlMap): void {
  const ratio = Math.min(3, Math.max(1, Math.round(window.devicePixelRatio || 1)))
  for (const status of Object.keys(EIM_STATUS_STYLE) as EimStatusKey[]) {
    const id = iconId(status)
    if (map.hasImage(id)) continue
    map.addImage(id, truckIcon(EIM_STATUS_STYLE[status].hex, ratio), { pixelRatio: ratio })
  }
}
// ============================================================ /truck icon

function toGeoJSON(
  vehicles: EimVehicle[], now: number, isDown?: (site: EimSite, vehicleId: string) => boolean,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: vehicles
      .filter((v): v is EimVehicle & { lat: number; lng: number } => v.lat != null && v.lng != null)
      .map((v) => {
        const status = eimStatusKey(v, now, isDown)
        const idleMin = v.idle_since
          ? Math.max(0, Math.round((now - new Date(v.idle_since).getTime()) / 60_000))
          : null
        return {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [v.lng, v.lat] },
          properties: {
            id: v.vehicle_id,
            status,
            icon: iconId(status),
            // 0 when the vendor has no heading rather than omitting the property --
            // maplibre's icon-rotate expression needs a number, and "nose north" is a
            // less misleading default than leaving the last-known rotation on screen.
            heading: v.heading ?? 0,
            soc: v.soc,
            speed: v.gps_speed,
            idleMin,
            idleKw: v.idle_power_w != null ? (v.idle_power_w / 1000).toFixed(1) : null,
          },
        }
      }),
  }
}

/** Fits to the current fleet once; callers decide when that "once" is (first data, not
 *  every 10s poll -- refitting on every refresh would yank the view out from under anyone
 *  who has panned in to look at one truck). */
function median(ns: number[]): number {
  const s = [...ns].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2
}

/** Fits to the DENSE cluster, not every point -- a truck genuinely away from the yard
 *  (confirmed live: one idling 5h+ turned up 15km from the port) must not be allowed to
 *  zoom the whole port out to a scale where the port itself is a speck. It's still a real
 *  marker on the map and still named in the Idle Alerts panel; it just doesn't drive the
 *  viewport. 3km: comfortably larger than any single terminal's own footprint (observed
 *  spread here is under 2km) and comfortably smaller than "actually left the port". */
const CLUSTER_RADIUS_KM = 3

function fitToVehicles(map: MlMap, vehicles: EimVehicle[]): void {
  const pts = vehicles.filter((v): v is EimVehicle & { lat: number; lng: number } => v.lat != null && v.lng != null)
  if (pts.length === 0) return
  const only = pts.length === 1 ? pts[0] : null
  if (only) {
    map.easeTo({ center: [only.lng, only.lat], zoom: 14, duration: 500 })
    return
  }
  const medLat = median(pts.map((v) => v.lat))
  const medLng = median(pts.map((v) => v.lng))
  const core = pts.filter((v) => {
    const dLatKm = (v.lat - medLat) * 111
    const dLngKm = (v.lng - medLng) * 105
    return Math.hypot(dLatKm, dLngKm) <= CLUSTER_RADIUS_KM
  })
  const fitset = core.length > 0 ? core : pts
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity
  for (const v of fitset) {
    minLng = Math.min(minLng, v.lng); maxLng = Math.max(maxLng, v.lng)
    minLat = Math.min(minLat, v.lat); maxLat = Math.max(maxLat, v.lat)
  }
  map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 56, duration: 500, maxZoom: 16 })
}

const MARK_BREAKDOWN_BTN_CLASS = 'eim-mark-breakdown-btn'

function popupHtml(p: Record<string, unknown>, detailed: boolean): string {
  const status = String(p.status ?? '')
  const soc = p.soc != null ? `${Math.round(Number(p.soc))}% SOC` : null
  const speed = p.speed != null ? `${p.speed} km/h` : null
  const idle = p.idleMin != null
    ? `Idle ${p.idleMin}m${p.idleKw != null ? ` · ${p.idleKw} kW draw` : ''}`
    : null
  return (
    `<div style="min-width:${detailed ? 180 : 140}px;font-family:Inter,sans-serif">`
    + `<div style="font:700 12px ui-monospace,monospace;color:#0F172A">${p.id}</div>`
    + `<div style="font:600 13px Inter,sans-serif;color:#0F172A;margin-top:4px">${EIM_STATUS_LABEL[status as EimStatusKey] ?? status}</div>`
    + `<div style="font:11px Inter,sans-serif;color:#64748B;margin-top:2px">${[soc, speed].filter(Boolean).join(' · ') || '—'}</div>`
    + (idle ? `<div style="font:11px Inter,sans-serif;color:#B45309;margin-top:2px">${idle}</div>` : '')
    + (detailed
        // Mark breakdown, straight from the click popup -- the one map interaction this
        // popup didn't have before. A plain button, not a real click handler here (this
        // is a raw HTML string handed to maplibre's Popup) -- the caller attaches a real
        // DOM listener to it after setHTML, keyed off this fixed class name.
        ? `<button type="button" class="${MARK_BREAKDOWN_BTN_CLASS}" data-vehicle-id="${p.id}"
             style="margin-top:8px;width:100%;border:1px solid rgba(244,63,94,0.30);
             background:${status === 'breakdown' ? 'rgba(244,63,94,0.10)' : '#fff'};
             color:#9F1239;font:600 11px Inter,sans-serif;border-radius:8px;padding:6px 8px;
             cursor:pointer">${status === 'breakdown' ? 'Update breakdown' : 'Mark breakdown'}</button>`
          + netradyneCameraButtonHtml()
        : `<div style="font:10px Inter,sans-serif;color:#94A3B8;margin-top:4px">click for details</div>`)
    + `</div>`
  )
}

function PortMap({ site, label, vehicles, tall = false, isDown, onMarkBreakdown, focusVehicleId }: {
  site: EimSite; label: string; vehicles: EimVehicle[]; tall?: boolean
  isDown?: (site: EimSite, vehicleId: string) => boolean
  onMarkBreakdown?: (site: EimSite, vehicleId: string) => void
  /** Flies to and opens this vehicle's popup -- set from an alert row click (see
   *  EimShell.tsx) instead of opening the acknowledge modal; triage now owns ack. */
  focusVehicleId?: string | null
}): JSX.Element {
  const container = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<MlMap | null>(null)
  const vehiclesRef = useRef<EimVehicle[]>(vehicles)
  const fittedRef = useRef(false)
  const isDownRef = useRef(isDown)
  // Ref, not a direct closure -- the 'load' listener below is registered once (empty
  // effect deps) and would otherwise capture whichever onMarkBreakdown existed on the
  // FIRST render forever. Same pattern StarMap.tsx's onVehicleClickRef uses.
  const onMarkBreakdownRef = useRef(onMarkBreakdown)
  // Set once inside the 'load' handler below, called both by the map's own click
  // handler and by the focusVehicleId effect further down -- one popup-opening path,
  // not two copies of the same HTML-building logic.
  const openVehiclePopupRef = useRef<((coords: [number, number], props: Record<string, unknown>) => void) | null>(null)
  vehiclesRef.current = vehicles
  isDownRef.current = isDown
  onMarkBreakdownRef.current = onMarkBreakdown

  const [mapLoaded, setMapLoaded] = useState(false)

  useEffect(() => {
    if (!container.current) return
    ensureWorker()
    const basemap = rasterBasemap('osm')
    if (!basemap) return
    const map = new MlMap({
      container: container.current,
      style: buildRasterStyle(basemap),
      center: SITE_FALLBACK_CENTER[site],
      zoom: 11,
      attributionControl: { compact: true },
    })
    mapRef.current = map
    map.addControl(new NavigationControl({ showCompass: false }), 'top-right')
    map.addControl(new ScaleControl({ unit: 'metric' }), 'bottom-left')

    const hoverPopup = new Popup({ closeButton: false, closeOnClick: false, offset: 18 })
    const clickPopup = new Popup({ closeButton: true, closeOnClick: false, offset: 22 })

    map.on('load', () => {
      registerTruckIcons(map)
      map.addSource('eim-trucks', {
        type: 'geojson', data: toGeoJSON(vehiclesRef.current, Date.now(), isDownRef.current),
      })
      map.addLayer({
        id: 'eim-trucks',
        type: 'symbol',
        source: 'eim-trucks',
        layout: {
          'icon-image': ['get', 'icon'],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-rotate': ['get', 'heading'],
          'icon-rotation-alignment': 'map',
          'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.32, 14, 0.5, 17, 0.75],
        },
      })
      fitToVehicles(map, vehiclesRef.current)
      fittedRef.current = true
      setMapLoaded(true)

      map.on('mouseenter', 'eim-trucks', (e) => {
        map.getCanvas().style.cursor = 'pointer'
        const f = e.features?.[0]
        if (!f || f.geometry.type !== 'Point') return
        hoverPopup.setLngLat(f.geometry.coordinates as [number, number])
          .setHTML(popupHtml(f.properties ?? {}, false)).addTo(map)
      })
      map.on('mouseleave', 'eim-trucks', () => {
        map.getCanvas().style.cursor = ''
        hoverPopup.remove()
      })
      function openVehiclePopup(coords: [number, number], props: Record<string, unknown>) {
        hoverPopup.remove()
        clickPopup.setLngLat(coords).setHTML(popupHtml(props, true)).addTo(map)
        // setHTML above replaces the popup's DOM every time, so the listener has to be
        // (re-)attached after each open, not once -- the previous element (and its
        // listener) is already gone by the time this runs.
        const id = props.id as string | undefined
        const popupEl = clickPopup.getElement()
        const btn = popupEl?.querySelector(`.${MARK_BREAKDOWN_BTN_CLASS}`)
        if (btn && id) {
          btn.addEventListener('click', () => {
            clickPopup.remove()
            onMarkBreakdownRef.current?.(site, id)
          })
        }
        if (popupEl && id) attachNetradyneCameraHandlers(popupEl, site, id)
      }
      openVehiclePopupRef.current = openVehiclePopup

      map.on('click', 'eim-trucks', (e) => {
        const f = e.features?.[0]
        if (!f || f.geometry.type !== 'Point') return
        openVehiclePopup(f.geometry.coordinates as [number, number], f.properties ?? {})
      })
      map.on('click', (e) => {
        if (!map.queryRenderedFeatures(e.point, { layers: ['eim-trucks'] }).length) clickPopup.remove()
      })
    })

    return () => {
      map.remove()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !map.isStyleLoaded()) return
    const src = map.getSource('eim-trucks') as GeoJSONSource | undefined
    if (!src) return
    src.setData(toGeoJSON(vehicles, Date.now(), isDown))
    if (!fittedRef.current && vehicles.some((v) => v.lat != null && v.lng != null)) {
      fitToVehicles(map, vehicles)
      fittedRef.current = true
    }
  }, [vehicles, isDown])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded || !focusVehicleId) return
    const v = vehiclesRef.current.find((x) => x.vehicle_id === focusVehicleId)
    if (!v || v.lat == null || v.lng == null) return
    const now = Date.now()
    const status = eimStatusKey(v, now, isDownRef.current)
    const idleMin = v.idle_since ? Math.max(0, Math.round((now - new Date(v.idle_since).getTime()) / 60_000)) : null
    map.flyTo({ center: [v.lng, v.lat], zoom: Math.max(map.getZoom(), 14), duration: 600 })
    openVehiclePopupRef.current?.([v.lng, v.lat], {
      id: v.vehicle_id,
      status,
      soc: v.soc,
      speed: v.gps_speed,
      idleMin,
      idleKw: v.idle_power_w != null ? (v.idle_power_w / 1000).toFixed(1) : null,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusVehicleId, mapLoaded])

  return (
    <Panel
      title={`${label} · live positions${vehicles.length ? ` · ${vehicles.length}` : ''}`}
      pad={false}
      className={tall ? 'h-[560px]' : 'h-[440px]'}
    >
      <div className="relative h-full w-full">
        <div ref={container} className="h-full w-full" />
      </div>
    </Panel>
  )
}

/**
 * GTI and JM Baxi are hundreds of km apart (Mumbai vs. Kandla), so this is always two
 * independent maps, never one shared viewport -- picking a site narrows to ONE full-width
 * map rather than leaving the other panel visibly empty beside it.
 */
export function EimPortsMap({ vehicles, site, isDown, onMarkBreakdown, focusVehicle }: {
  vehicles: EimVehicle[]; site: EimSite | 'all'
  isDown?: (site: EimSite, vehicleId: string) => boolean
  onMarkBreakdown?: (site: EimSite, vehicleId: string) => void
  /** Set from an alert row click (see EimShell.tsx) -- flies to and opens that vehicle's
   *  popup instead of an acknowledge modal; triage now owns ack. */
  focusVehicle?: { site: EimSite; vehicleId: string } | null
}): JSX.Element {
  const gti = vehicles.filter((v) => v.site === 'gti')
  const jmBaxi = vehicles.filter((v) => v.site === 'jm_baxi')
  const gtiFocus = focusVehicle?.site === 'gti' ? focusVehicle.vehicleId : null
  const jmBaxiFocus = focusVehicle?.site === 'jm_baxi' ? focusVehicle.vehicleId : null
  if (site === 'gti') {
    return <PortMap site="gti" label="GTI" vehicles={gti} tall isDown={isDown} onMarkBreakdown={onMarkBreakdown} focusVehicleId={gtiFocus} />
  }
  if (site === 'jm_baxi') {
    return <PortMap site="jm_baxi" label="JM Baxi" vehicles={jmBaxi} tall isDown={isDown} onMarkBreakdown={onMarkBreakdown} focusVehicleId={jmBaxiFocus} />
  }
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <PortMap site="gti" label="GTI" vehicles={gti} isDown={isDown} onMarkBreakdown={onMarkBreakdown} focusVehicleId={gtiFocus} />
      <PortMap site="jm_baxi" label="JM Baxi" vehicles={jmBaxi} isDown={isDown} onMarkBreakdown={onMarkBreakdown} focusVehicleId={jmBaxiFocus} />
    </div>
  )
}
